import { OrgsGeneralInfo } from '@/models/Orgs'
import { isBlockedUrl, getBlockedUrlMessage } from '@/utils/urlValidation'
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  TextField,
  Box,
  Button,
} from '@mui/material'

import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { useState } from 'react'

interface OrgGeneralInfoFormProps {
  generalFormData: OrgsGeneralInfo
  setGeneralFormData: (org: OrgsGeneralInfo) => void
}

export default function OrgGeneralInfoForm({
  generalFormData,
  setGeneralFormData,
}: OrgGeneralInfoFormProps) {
  const [addSecondaryContact, setAddSecondaryContact] = useState(
    generalFormData.secondary_contact_name ? true : false
  )

  const handleGeneralInfoInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target
    const newFormData = { ...generalFormData, [name]: value }
    setGeneralFormData(newFormData)
  }

  function handleSecondaryContactToggle() {
    if (!addSecondaryContact) {
      setAddSecondaryContact(true)
    }
    if (addSecondaryContact) {
      if (
        generalFormData.secondary_contact_name?.trim() === '' &&
        generalFormData.secondary_contact_phone?.trim() === '' &&
        generalFormData.secondary_contact_email?.trim() === ''
      ) {
        setAddSecondaryContact(false)
      } else {
        const confirmed = window.confirm(
          'Are you sure you want to remove the secondary contact?'
        )
        if (!confirmed) return
        setAddSecondaryContact(false)
        setGeneralFormData({
          ...generalFormData,
          secondary_contact_name: '',
          secondary_contact_phone: '',
          secondary_contact_email: '',
        })
      }
    }
  }

  return (
    <>
      <Typography
        component="label"
        sx={{
          fontSize: '16px',
          fontWeight: 500,
          color: '#333333',
          display: 'block',
          mt: 2,
        }}
      >
        General Info
      </Typography>
      <Typography variant="body2" sx={{ color: '#666666' }}>
        Here you can provide general information about your organisation to use
        in the application process.
      </Typography>
      <Typography variant="body2" sx={{ color: '#666666' }}>
        This section is optional, but will help us to generate answers for your
        basic information on applications.
      </Typography>
      <Accordion
        sx={{
          mt: 2,
          borderRadius: 2,
          '& .MuiAccordionSummary-root': {
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography sx={{ fontWeight: 500, fontSize: '16px' }}>
            Application Information
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ my: 2 }}>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              color: '#333333',
              display: 'block',
            }}
          >
            Primary Contact
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <Typography
              component="label"
              htmlFor="primary_contact_name"
              sx={{
                fontSize: '16px',
                color: '#333333',
                display: 'block',
              }}
            >
              Name
            </Typography>
            <TextField
              id="primary_contact_name"
              name="primary_contact_name"
              value={generalFormData.primary_contact_name}
              onChange={handleGeneralInfoInputChange}
              fullWidth
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: '#ffffff',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1C3045',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1C3045',
                    borderWidth: 2,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '16px',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '14px',
                  color: '#666666',
                  mt: 1,
                },
              }}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Typography
              component="label"
              htmlFor="primary_contact_email"
              sx={{
                fontSize: '16px',
                color: '#333333',
                display: 'block',
              }}
            >
              Email
            </Typography>
            <TextField
              id="primary_contact_email"
              name="primary_contact_email"
              value={generalFormData.primary_contact_email}
              onChange={handleGeneralInfoInputChange}
              fullWidth
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: '#ffffff',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1C3045',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1C3045',
                    borderWidth: 2,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '16px',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '14px',
                  color: '#666666',
                  mt: 1,
                },
              }}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Typography
              component="label"
              htmlFor="primary_contact_phone"
              sx={{
                fontSize: '16px',
                color: '#333333',
                display: 'block',
              }}
            >
              Phone
            </Typography>
            <TextField
              id="primary_contact_phone"
              name="primary_contact_phone"
              value={generalFormData.primary_contact_phone}
              onChange={handleGeneralInfoInputChange}
              fullWidth
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: '#ffffff',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1C3045',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1C3045',
                    borderWidth: 2,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '16px',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '14px',
                  color: '#666666',
                  mt: 1,
                },
              }}
            />
          </FormControl>

          {addSecondaryContact && (
            <Box sx={{ mt: 4 }}>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: '#333333',
                  display: 'block',
                }}
              >
                Secondary Contact
              </Typography>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <Typography
                  component="label"
                  htmlFor="secondary_contact_name"
                  sx={{
                    fontSize: '16px',
                    color: '#333333',
                    display: 'block',
                  }}
                >
                  Name
                </Typography>
                <TextField
                  id="secondary_contact_name"
                  name="secondary_contact_name"
                  value={generalFormData.secondary_contact_name}
                  onChange={handleGeneralInfoInputChange}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: 2,
                      backgroundColor: '#ffffff',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1C3045',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1C3045',
                        borderWidth: 2,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '16px',
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '14px',
                      color: '#666666',
                      mt: 1,
                    },
                  }}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <Typography
                  component="label"
                  htmlFor="secondary_contact_email"
                  sx={{
                    fontSize: '16px',
                    color: '#333333',
                    display: 'block',
                  }}
                >
                  Email
                </Typography>
                <TextField
                  id="secondary_contact_email"
                  name="secondary_contact_email"
                  value={generalFormData.secondary_contact_email}
                  onChange={handleGeneralInfoInputChange}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: 2,
                      backgroundColor: '#ffffff',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1C3045',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1C3045',
                        borderWidth: 2,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '16px',
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '14px',
                      color: '#666666',
                      mt: 1,
                    },
                  }}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <Typography
                  component="label"
                  htmlFor="secondary_contact_phone"
                  sx={{
                    fontSize: '16px',
                    color: '#333333',
                    display: 'block',
                  }}
                >
                  Phone
                </Typography>
                <TextField
                  id="secondary_contact_phone"
                  name="secondary_contact_phone"
                  value={generalFormData.secondary_contact_phone}
                  onChange={handleGeneralInfoInputChange}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: 2,
                      backgroundColor: '#ffffff',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1C3045',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1C3045',
                        borderWidth: 2,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '16px',
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '14px',
                      color: '#666666',
                      mt: 1,
                    },
                  }}
                />
              </FormControl>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 0.5,
              mb: 2,
            }}
          >
            <Button
              variant="text"
              disableRipple
              color="primary"
              onClick={() => handleSecondaryContactToggle()}
              sx={{
                borderColor: '#1C3045',
                color: '#1C3045',
                textTransform: 'none',
                '&:focus': {
                  backgroundColor: '#e8e8e8',
                },
              }}
            >
              {addSecondaryContact ? (
                <CloseIcon sx={{ mr: 1 }} />
              ) : (
                <AddIcon sx={{ mr: 1 }} />
              )}
              {addSecondaryContact
                ? 'Remove secondary contact'
                : 'Add another contact'}
            </Button>
          </Box>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <Typography
              component="label"
              htmlFor="physical_address"
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: '#333333',
                display: 'block',
              }}
            >
              Physical Address
            </Typography>
            <TextField
              id="physical_address"
              name="physical_address"
              value={generalFormData.physical_address}
              onChange={handleGeneralInfoInputChange}
              fullWidth
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: '#ffffff',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1C3045',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1C3045',
                    borderWidth: 2,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '16px',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '14px',
                  color: '#666666',
                  mt: 1,
                },
              }}
            />
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <Typography
              component="label"
              htmlFor="postal_address"
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: '#333333',
                display: 'block',
              }}
            >
              Postal Address
            </Typography>
            <TextField
              id="postal_address"
              name="postal_address"
              value={generalFormData.postal_address}
              onChange={handleGeneralInfoInputChange}
              fullWidth
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: '#ffffff',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1C3045',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1C3045',
                    borderWidth: 2,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '16px',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '14px',
                  color: '#666666',
                  mt: 1,
                },
              }}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Typography
              component="label"
              htmlFor="website"
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: '#333333',
                display: 'block',
              }}
            >
              Organisation Website
            </Typography>
            <TextField
              id="website"
              name="website"
              value={generalFormData.website}
              onChange={handleGeneralInfoInputChange}
              placeholder="https://www.example.org"
              fullWidth
              variant="outlined"
              error={isBlockedUrl(generalFormData.website ?? '')}
              helperText={
                isBlockedUrl(generalFormData.website ?? '')
                  ? getBlockedUrlMessage()
                  : undefined
              }
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: '#ffffff',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1C3045',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1C3045',
                    borderWidth: 2,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '16px',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '14px',
                  color: '#666666',
                  mt: 1,
                },
              }}
            />
          </FormControl>
        </AccordionDetails>
      </Accordion>
    </>
  )
}
